import {type ComponentProps, type ForwardedRef, forwardRef, useMemo, useRef} from 'react';

import {useIntl} from 'react-intl';

import {useTooltipTriggerState} from '@react-stately/tooltip';

import {useTooltipTrigger} from '@react-aria/tooltip';

import classNames from 'classnames';

import {keyBy} from 'lodash';

import {InputTag} from '@time-webkit/all/organisms/input-tag';
import {InfoOutlineIcon} from '@time-webkit/all/icons/info-outline';
import {Tooltip, TooltipTextSecondary} from '@time-webkit/all/atoms/tooltip';

import {type UserProfile} from '@mattermost/types/users';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {type InvitationError} from '../../invitation_error';

import {TagProfile} from './tag-profile';

import styles from './input-invite-by-email.module.css';

type Props = ComponentProps<typeof InputTag> & {
    profiles?: UserProfile[];
    invitationErrors?: InvitationError[];
    allowedCurrentTeamDomains?: string[];
    allowedSystemDomains?: string[];
    loadingStatuses?: Record<string, boolean>;
}

type TagVariant = ComponentProps<typeof TagProfile>['variant']

const DEFAULT_ERRORS: InvitationError[] = [];
const DEFAULT_PROFILES: UserProfile[] = [];
const DEFAULT_LOADING_STATUS: Record<string, boolean> = {};
const DEFAULT_ALLOWED_DOMAINS: string[] = [];
const MAX_DOMAINS = 10;

export const InputInviteByEmail = forwardRef(
    (
        {
            tags,
            profiles = DEFAULT_PROFILES,
            allowedCurrentTeamDomains = DEFAULT_ALLOWED_DOMAINS,
            allowedSystemDomains = DEFAULT_ALLOWED_DOMAINS,
            loadingStatuses = DEFAULT_LOADING_STATUS,
            invitationErrors = DEFAULT_ERRORS,
            error,
            maxLength,
            ...props
        }: Props,
        ref: ForwardedRef<HTMLInputElement>,
    ) => {
        const intl = useIntl();
        const bodySTrueTypography = useTypography({size: TypographySize.BodySTrue, noDefaultColor: true});
        const tooltipIconRef = useRef<HTMLSpanElement>(null);
        const tooltipTriggerState = useTooltipTriggerState({delay: 200});
        const {triggerProps, tooltipProps} = useTooltipTrigger({}, tooltipTriggerState, tooltipIconRef);

        const errorsByEmail = useMemo(() => keyBy(invitationErrors, (error) => error.email), [invitationErrors]);
        const profilesByEmail = useMemo(() => keyBy(profiles, (profile) => profile.email), [profiles]);

        const isInvalid = useMemo(() => {
            return invitationErrors.some((error) => error?.type === 'error');
        }, [invitationErrors]);

        const renderTag = (label: string) => {
            const profile = profilesByEmail[label];
            const error = errorsByEmail[label];
            const loading = loadingStatuses[label];

            return (
                <TagProfile
                    key={label}
                    label={label}
                    profile={profile}
                    loading={loading}
                    variant={getTagVariant(error?.type)}
                    tooltip={error?.getLocalizedMessage(intl)}
                    maxLength={maxLength}
                />
            );
        };

        const hasCurrentTeamDomains = allowedCurrentTeamDomains.length > 0;
        const domainsList = hasCurrentTeamDomains ? allowedCurrentTeamDomains : allowedSystemDomains;

        return (
            <>
                <InputTag
                    {...props}
                    ref={ref}
                    tags={tags}
                    maxLength={maxLength}
                    error={error || isInvalid}
                    formatLabel={(label) => label.toLowerCase()}
                    iconRight={
                        domainsList.length > 0 ? (
                            <span
                                {...triggerProps}
                                ref={tooltipIconRef}
                                className={styles.iconInfo}
                                data-testid='domains-tooltip'
                            >
                                <InfoOutlineIcon
                                    width={16}
                                    height={16}
                                />
                            </span>) : null
                    }
                >
                    {tags.map(renderTag)}
                </InputTag>
                {tooltipTriggerState.isOpen && (
                    <Tooltip
                        {...tooltipProps}
                        state={tooltipTriggerState}
                        targetRef={tooltipIconRef}
                        className={styles.tooltip}
                    >
                        <p className={classNames(styles.domainListTitle, bodySTrueTypography)}>
                            {intl.formatMessage({
                                id: 'invite.members.allowed_domains',
                                defaultMessage: 'Allowed domains:',
                            }, {count: domainsList.length})}
                        </p>

                        {domainsList.length > 1 ? (
                            <TooltipTextSecondary align='left'>
                                <ul className={styles.domainList}>
                                    {domainsList.slice(0, MAX_DOMAINS).map((domain) => (
                                        <li key={domain}>{`∙ @${domain}`}</li>
                                    ))}
                                </ul>

                                {domainsList.length > MAX_DOMAINS && (
                                    <p className={styles.domainsNote}>
                                        {intl.formatMessage({
                                            id: 'invite.members.domains_sys_console_note',
                                            defaultMessage: 'The full list of domains in the system console',
                                        })}
                                        {hasCurrentTeamDomains && intl.formatMessage({
                                            id: 'invite.members.domains_team_settings_note',
                                            defaultMessage: ' and team settings',
                                        })}
                                    </p>
                                )}
                            </TooltipTextSecondary>
                        ) : (
                            <p className={classNames(styles.domainListSubtitle, bodySTrueTypography)}>
                                {`@${domainsList[0]}`}
                            </p>
                        )}
                    </Tooltip>
                )}
            </>
        );
    },
);

function getTagVariant(errorType?: InvitationError['type']): TagVariant {
    switch (errorType) {
    case 'error':
        return 'error';
    case 'warning':
        return 'warning';
    default:
        return 'default';
    }
}

InputInviteByEmail.displayName = 'InputInviteByEmail';
